import React, { useEffect, useRef } from 'react';
import { BAND_TONE } from '../../lib/leiConditions';

/**
 * Repositioned LEI pillars as condition stages (Rec 4). Same pillar,
 * same score — but framed by its place in the navigation journey.
 * Clicking a card drills into the existing pillar detail view.
 * `highlightStage` (from a Home deep-link) rings + scrolls its card.
 */
export function ConditionsGrid({ conditions, onSelect, highlightStage }) {
  const refs = useRef({});

  useEffect(() => {
    if (highlightStage && refs.current[highlightStage]) {
      refs.current[highlightStage].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightStage, conditions]);

  if (!conditions?.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="conditions-grid">
      {conditions.map((c) => (
        <button
          key={c.key}
          ref={(el) => { refs.current[c.key] = el; }}
          type="button"
          onClick={() => onSelect?.(c.pillarId)}
          data-testid={`condition-card-${c.key}`}
          className={`text-left rounded-xl border bg-white p-4 hover:border-slate-300 hover:shadow-sm transition-all ${
            highlightStage === c.key
              ? 'border-cyan-400 ring-2 ring-cyan-300 shadow-md'
              : 'border-slate-200'
          }`}
          style={{ borderTopColor: c.color, borderTopWidth: 3 }}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: c.color }}>
            {c.label} Stage
          </p>
          <p className="text-sm font-semibold text-slate-800 mt-0.5 leading-tight">{c.pillarShortName}</p>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-extrabold tabular-nums text-slate-900">
              {c.score != null ? Math.round(c.score) : '—'}
            </span>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${BAND_TONE[c.band.tone]}`}>
              {c.band.label}
            </span>
          </div>
          <div className="h-1.5 mt-2 rounded-full bg-slate-200/70 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, c.score ?? 0))}%`, backgroundColor: c.color }} />
          </div>
          {c.pillarWeight != null && (
            <p className="text-[10px] text-slate-400 mt-1.5">{Math.round(c.pillarWeight * 100)}% LEI weight · tap to drill in</p>
          )}
        </button>
      ))}
    </div>
  );
}
